import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, Grid, Tab, Tabs, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { authStore } from '@/store/authStore'
import { usuarioService } from '@/services/usuarioService'
import { convitesService } from '@/services/convitesService'
import { UsuarioAdmin } from '@/types/usuario'
import { ConvitePendente } from '@/types/convite'
import { CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import ConvidarUsuarioDialog from '@/pages/usuarios/ConvidarUsuarioDialog'
import UsuariosListPage from '@/pages/usuarios/UsuariosListPage'
import SaneamentoOperadorPage from '@/pages/usuarios/SaneamentoOperadorPage'
import CandidaturasPage from '@/pages/candidatura/CandidaturasPage'
import TenantsListPage from '@/pages/tenants/TenantsListPage'
import ConteudoInstitucionalPage from '@/pages/onboarding/ConteudoInstitucionalPage'

/** Valores de `?aba=` — os MESMOS que o A02 registrou (links antigos continuam valendo). */
const ABAS: { valor: string; rotulo: string }[] = [
  { valor: '', rotulo: 'Usuários' },
  { valor: 'candidaturas', rotulo: 'Candidaturas' },
  { valor: 'saneamento-operador', rotulo: 'Saneamento' },
  { valor: 'tenants', rotulo: 'Empresas' },
  { valor: 'conteudo-primeiro-acesso', rotulo: 'Conteúdo do 1º acesso' },
]

const rotuloCanal = (codigo?: string | null) =>
  codigo ? CANAL_LABEL[codigo as CanalComercial] ?? codigo : '—'

/**
 * Usuários e Acessos (Figma 28:576) — a casca com abas do administrador
 * (Onda 2 / A05). Cada aba monta a página EXISTENTE (mesmo `?aba=` do A02).
 *
 * KPIs só na aba Usuários: citam a MESMA listagem (`usuarioService.listar`)
 * que a tabela logo abaixo exibe. "Convites pendentes" (Onda 7.3 / A13) cita a
 * lista de convites emitidos e ainda não usados — a mesma que a tabela
 * "Convite pendente → Reenviar" mostra. Nunca um número inventado: sem fonte, "—".
 */
export const UsuariosAcessosPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const { enqueueSnackbar } = useSnackbar()
  const isAdmin = useMemo(
    () => !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'),
    [usuario],
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const abaParam = searchParams.get('aba') ?? ''
  const aba = ABAS.some((a) => a.valor === abaParam) ? abaParam : ''

  const [usuarios, setUsuarios] = useState<UsuarioAdmin[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [pendentes, setPendentes] = useState<ConvitePendente[] | null>(null)
  const [carregandoPendentes, setCarregandoPendentes] = useState(false)
  const [erroPendentes, setErroPendentes] = useState<string | null>(null)
  const [convidando, setConvidando] = useState(false)
  const [reenviandoId, setReenviandoId] = useState<string | null>(null)

  const carregarPendentes = useCallback(async () => {
    if (!isAdmin) return
    setCarregandoPendentes(true)
    setErroPendentes(null)
    try {
      setPendentes(await convitesService.listarPendentes())
    } catch (e) {
      setErroPendentes(e instanceof Error ? e.message : 'Falha ao listar convites pendentes')
      setPendentes(null)
    } finally {
      setCarregandoPendentes(false)
    }
  }, [isAdmin])

  const reenviar = async (convite: ConvitePendente) => {
    setReenviandoId(convite.conviteId)
    try {
      const novo = await convitesService.reenviar(convite.conviteId)
      enqueueSnackbar(
        novo.emailEnviado
          ? `Convite reenviado para ${novo.emailDestinatario ?? convite.email}`
          : 'Convite reemitido, mas o e-mail não saiu — copie o link na aba Candidaturas',
        { variant: novo.emailEnviado ? 'success' : 'warning' }
      )
      await carregarPendentes()
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message ?? 'Falha ao reenviar convite', {
        variant: 'error',
      })
    } finally {
      setReenviandoId(null)
    }
  }

  const carregar = useCallback(async () => {
    if (!isAdmin) return
    setCarregando(true)
    setErro(null)
    try {
      setUsuarios(await usuarioService.listar())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao listar usuários')
      setUsuarios(null)
    } finally {
      setCarregando(false)
    }
  }, [isAdmin])

  useEffect(() => {
    if (aba === '') {
      carregar()
      carregarPendentes()
    }
  }, [aba, carregar, carregarPendentes])

  const trocarAba = (valor: string) => {
    const proximos = new URLSearchParams(searchParams)
    if (valor) proximos.set('aba', valor)
    else proximos.delete('aba')
    setSearchParams(proximos, { replace: true })
  }

  const cabecalho = (
    <PageHeader
      title="Usuários e Acessos"
      eyebrow="Administração"
      subtitle="Usuários, candidaturas, empresas e conteúdo do primeiro acesso"
      acao={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!isAdmin}
          onClick={() => setConvidando(true)}
        >
          Convidar usuário
        </Button>
      }
    />
  )

  if (usuario && !isAdmin) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="semPermissao"
          detalhe="A gestão de usuários e acessos é restrita a administradores."
        />
      </Box>
    )
  }

  const ativos = (usuarios ?? []).filter((u) => u.enabled).length
  const canais = new Set((usuarios ?? []).map((u) => u.tipoCanal).filter(Boolean)).size

  return (
    <Box>
      {cabecalho}

      <Tabs
        value={aba}
        onChange={(_, v) => trocarAba(v as string)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {ABAS.map((a) => (
          <Tab key={a.valor} value={a.valor} label={a.rotulo} />
        ))}
      </Tabs>

      {aba === '' && (
        <>
          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                variante="escura"
                label="Usuários"
                carregando={carregando}
                valor={usuarios ? usuarios.length : '—'}
                legenda="Mesma listagem da tabela abaixo"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Ativos"
                carregando={carregando}
                valor={usuarios ? ativos : '—'}
                legenda="Habilitados no Keycloak"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Convites pendentes"
                carregando={carregandoPendentes}
                valor={pendentes ? pendentes.length : '—'}
                legenda="Emitidos e ainda não usados — mesma lista abaixo"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                label="Canais"
                carregando={carregando}
                valor={usuarios ? canais : '—'}
                legenda="Canais distintos entre os usuários listados"
              />
            </Grid>
          </Grid>
        </>
      )}

      {aba === '' && (
        <Box sx={{ mb: 3 }} data-testid="convites-pendentes">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Convites pendentes
          </Typography>
          {erroPendentes && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erroPendentes}
            </Alert>
          )}
          {carregandoPendentes && !pendentes ? (
            <EstadoDeLista estado="carregando" minHeight={120} />
          ) : pendentes && pendentes.length === 0 ? (
            <EstadoDeLista
              estado="vazio"
              mensagem="Nenhum convite pendente"
              detalhe="Todo convite emitido já foi usado, expirou ou foi revogado."
              minHeight={120}
            />
          ) : pendentes ? (
            <TabelaAcao<ConvitePendente>
              aria-label="Convites pendentes"
              colunas={[
                { titulo: 'Nome', render: (c) => c.nome },
                { titulo: 'E-mail', render: (c) => c.email },
                { titulo: 'Canal', render: (c) => rotuloCanal(c.tipoCanal) },
                {
                  titulo: 'Origem',
                  render: (c) => (
                    <Chip
                      size="small"
                      label={c.conviteDireto ? 'Convite direto' : 'Candidatura'}
                      variant="outlined"
                    />
                  ),
                },
                {
                  titulo: 'Expira em',
                  render: (c) => new Date(c.expiraEm).toLocaleString('pt-BR'),
                },
              ]}
              linhas={pendentes}
              chaveDaLinha={(c) => c.conviteId}
              onAbrir={(c) => {
                if (!reenviandoId) reenviar(c)
              }}
              renderAcao={(c) =>
                reenviandoId === c.conviteId ? 'Reenviando…' : 'Convite pendente → Reenviar'
              }
            />
          ) : null}
        </Box>
      )}

      {aba === '' && <UsuariosListPage />}
      {aba === 'candidaturas' && <CandidaturasPage />}
      {aba === 'saneamento-operador' && <SaneamentoOperadorPage />}
      {aba === 'tenants' && <TenantsListPage />}
      {aba === 'conteudo-primeiro-acesso' && <ConteudoInstitucionalPage />}

      <ConvidarUsuarioDialog
        aberto={convidando}
        escolheTenant={isAdmin}
        onFechar={() => setConvidando(false)}
        onEmitido={carregarPendentes}
      />
    </Box>
  )
}

export default UsuariosAcessosPage
